'use strict'

import config from '../config/index'

/**
 * Merge the webchatSlots received from the client with the existing slots in the state
 * If a slot already exists, it will be overwritten by the new value
 * @param {Object} existingSlots - Existing slots in the state [{slug: value}]
 * @param {Object} slotsToAdd - Slots received from the client {slug: value}
 * @returns {Array} Merged slots
 */
export const mergeWithStateWebchatSlots = (existingSlots, slotsToAdd) => {
  const existingSlotsMap = existingSlots.reduce((acc, obj) => ({ ...acc, ...obj }), {})
  const mergedSlots = { ...existingSlotsMap, ...slotsToAdd }
  return Object.entries(mergedSlots).map(([key, value]) => ({ [key]: value }))
}

/**
 * Get a webchat slot value by slug from the array format used in the store.
 * @param {Object[]} webchatSlots - Existing slots in the state [{slug: value}]
 * @param {String|null} slotSlug
 * @returns {*|undefined}
 */
export const getWebchatSlotValue = (webchatSlots, slotSlug) => {
  if (!Array.isArray(webchatSlots) || !slotSlug) {
    return undefined
  }

  const matchingSlot = webchatSlots.find(slot => slot && Object.prototype.hasOwnProperty.call(slot, slotSlug))

  return matchingSlot ? matchingSlot[slotSlug] : undefined
}

/**
 * Check whether a slot value can be used as an identification value.
 * @param {*} slotValue
 * @returns {Boolean}
 */
export const isUsableWebchatSlotValue = slotValue => {
  if (slotValue === null || typeof slotValue === 'undefined') {
    return false
  }

  if (typeof slotValue === 'string') {
    return !!slotValue.trim()
  }

  return true
}

/**
 * Extract the slots values from the postMessage event data based on the postMessageSlotsProperties
 * @param {Array} postMessageSlotsProperties - Array of slot properties {slug, fillFromPostMessageObjectKey: [{locale, objectKey}]}
 * @param {Object} eventData - Data received from the postMessage event
 * @param {String} locale - Current locale of the webchat
 * @returns {Object} Extracted slots {slug: value}
 */
export const extractClientPostMessageData = (postMessageSlotsProperties, eventData, locale) => {
  // client can fill in multiple slots from a single postMessage
  const postMessageSlots = {}

  postMessageSlotsProperties.forEach(slot => {
    const fillFromPostMessageObject = slot.fillFromPostMessageObjectKey.find(obj => obj.locale === locale)
    if (!fillFromPostMessageObject || !fillFromPostMessageObject.objectKey) {
      return
    }

    // The event is not matching our slot
    if (!(fillFromPostMessageObject.objectKey in eventData)) {
      return
    }
    postMessageSlots[slot.slug] = eventData[fillFromPostMessageObject.objectKey]
  })

  return postMessageSlots
}

/**
 * Trim white spaces, replace whitespaces and dashes with underscores, convert to lower case, and remove diacritics (accents)
 * @param {String} string - the string to manipulate
 * @return {String} the transformed string
 */
export const normalizeLowerCase = string => {
  string = ('' + string).trim() // Security cast + remove start and end whitespaces
  string = string.normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove the diacritics from string
  string = string.replace(/[\s-]+/g, '_') // replace other whitespaces and dashes by _
  return string.toLowerCase() // force lowercase
}

/**
 * Check if the webchat slot value is matching the trigger slot requirements
 * @param {String} slotValue - The webchat slot value
 * @param {Array} possibleSlotValues - The possible slot values defined in the trigger slot
 * @param {Array} excludedSlotValues - The excluded slot values defined in the trigger slot
 * @param {Boolean} canSlotValueBeEmpty - If true, the slot value can be empty
 * @param {Boolean} canSlotValueBeAnything - If true, the slot value can be anything
 * @returns {Boolean} True if the webchat slot value is matching the trigger slot requirements
 */
const isWebchatSlotValueMatchingRequirements = (
  slotValue,
  possibleSlotValues,
  excludedSlotValues,
  canSlotValueBeEmpty,
  canSlotValueBeAnything
) => {
  const normalizedSlotValue = slotValue === null || slotValue === undefined ? '' : normalizeLowerCase(slotValue)

  if ((canSlotValueBeEmpty && !normalizedSlotValue) || (canSlotValueBeAnything && normalizedSlotValue)) {
    return true
  }

  const isPossibleSlotValueRequirementMet =
    !possibleSlotValues.length || possibleSlotValues.includes(normalizedSlotValue)
  const isExcludedSlotValueRequirementMet =
    !excludedSlotValues.length || !excludedSlotValues.includes(normalizedSlotValue)

  return isPossibleSlotValueRequirementMet && isExcludedSlotValueRequirementMet
}

/**
 * Check if the trigger slot is matching the webchat slots
 * @param {Object} triggerSlot - The trigger slot
 * @param {Object} webchatSlots - The webchat slots {slug: value}
 * @returns {Boolean} True if the trigger slot is matching the webchat slots
 */
export const isTriggerSlotValueMatchingWebchatSlot = (triggerSlot, webchatSlots) => {
  // Slot not set properly, let's consider it not matching since it cannot be evaluated
  if (!triggerSlot || !triggerSlot.slotId || !triggerSlot.slotId.slug) {
    return false
  }

  const slotSlug = triggerSlot.slotId.slug
  const normalizedPossibleSlotValues = (triggerSlot.possibleSlotValues || []).map(value => normalizeLowerCase(value))
  const normalizedExcludedSlotValues = (triggerSlot.excludedSlotValues || []).map(value => normalizeLowerCase(value))

  // If the slot is not found in the webchat slots, we consider its value as empty
  if (!webchatSlots || !(slotSlug in webchatSlots)) {
    return triggerSlot.canSlotValueBeEmpty
  }

  return isWebchatSlotValueMatchingRequirements(
    webchatSlots[slotSlug],
    normalizedPossibleSlotValues,
    normalizedExcludedSlotValues,
    triggerSlot.canSlotValueBeEmpty,
    triggerSlot.canSlotValueBeAnything
  )
}

/**
 * Check if the context requirements defined in the menu item are met by the conversation context list
 *
 * @param {Object} menuItem - The menu item containing the context requirements. {contextRequirements: [{locale, slots: [{slotId: {slug}, possibleSlotValues, excludedSlotValues, canSlotValueBeEmpty, canSlotValueBeAnything}]}]}
 * @param {Array<Object>} webchatSlots - The conversation context list. [{slug: slotValue}]
 * @param {String} locale - Current locale of the webchat
 * @returns {Boolean} True if the context requirements are met, false otherwise
 */
export const areContextRequirementsMet = (menuItem, webchatSlots, locale) => {
  if (!menuItem.contextRequirements || !menuItem.contextRequirements.length) {
    return true
  }

  const localizedRequirements = menuItem.contextRequirements.find(requirement => requirement.locale === locale)

  if (!localizedRequirements || !localizedRequirements.slots || !localizedRequirements.slots.length) {
    return true
  }

  return localizedRequirements.slots.every(requirementSlot => {
    // requirementSlot not set properly, let's consider it not matching since it cannot be evaluated
    if (!requirementSlot || !requirementSlot.slotId || !requirementSlot.slotId.slug) {
      return false
    }

    const contextSlot = (webchatSlots || []).find(slotObject => requirementSlot.slotId.slug in slotObject)
    return isTriggerSlotValueMatchingWebchatSlot(requirementSlot, contextSlot)
  })
}

/**
 * Extract the free-mode slots from postMessageSlots for the current locale.
 * @param {Array} postMessageSlots - Array of slot definitions from CHAT_LOAD
 * @param {String} locale - Current locale of the webchat
 * @returns {Array<{slotSlug: string, objectKey: string}>}
 */
export const extractFreePostMessageSlots = (postMessageSlots, locale) => {
  if (!postMessageSlots || !postMessageSlots.length) {
    return []
  }
  return postMessageSlots
    .filter(slot => slot && slot.postMessageSlotFillingMode === config.POST_MESSAGE_SLOT_FILLING_MODES.FREE)
    .reduce((acc, slot) => {
      const fillFromPostMessageObjectKey = Array.isArray(slot.fillFromPostMessageObjectKey)
        ? slot.fillFromPostMessageObjectKey
        : []
      const localeEntry = fillFromPostMessageObjectKey.find(obj => obj && obj.locale === locale)
      if (!localeEntry || !localeEntry.objectKey) {
        return acc
      }
      acc.push({ slotSlug: slot.slug, objectKey: localeEntry.objectKey })
      return acc
    }, [])
}

/**
 * Build the slot:update payloads for a single post message event from its free-mode slots.
 * @param {Object} eventData - Post message event data keyed by objectKey
 * @param {Array<{slotSlug: string, objectKey: string}>} freePostMessageSlots
 * @returns {Array<{slotSlug: string, value: *}>}
 */
export const collectFreeSlotUpdatesForEvent = (eventData, freePostMessageSlots) => {
  if (!eventData || !freePostMessageSlots || !freePostMessageSlots.length) {
    return []
  }
  return freePostMessageSlots
    .filter(slot => eventData[slot.objectKey] !== undefined)
    .map(slot => ({ slotSlug: slot.slotSlug, value: eventData[slot.objectKey] }))
}

export default {
  extractClientPostMessageData,
  extractFreePostMessageSlots,
  mergeWithStateWebchatSlots,
  collectFreeSlotUpdatesForEvent,
  getWebchatSlotValue,
  isUsableWebchatSlotValue,
  isTriggerSlotValueMatchingWebchatSlot,
  areContextRequirementsMet
}
